/**
 * Base Repository Pattern
 * 
 * Provides complete CRUD operations for all entities using Dexie/IndexedDB
 * NO STUBS - Full implementation
 * 
 * Maritime Compliance Features:
 * - Soft deletes only (deletedAt timestamp) - no hard deletes
 * - Immutable audit trail for all changes
 * - Append-only logging (never modify or delete audit records)
 * - Complete change history for regulatory compliance
 */

import { Table } from 'dexie';
import { AuditTrailService } from './auditTrail';

export interface QueryOptions {
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, any>; // Supports any field including logbookType, status, etc.
  logbookType?: string; // Convenience shortcut for filters.logbookType
  startDate?: string | Date; // For date range filtering
  endDate?: string | Date; // For date range filtering
}

export interface QueryResult<T> {
  data: T[];
  total: number;
  hasMore: boolean;
}

/**
 * Base entity interface - all entities should have these fields
 */
export interface BaseEntity {
  id: string;
  deletedAt?: Date | null;
  createdAt: Date;
  updatedAt: Date;
  organizationId?: string;
  vesselId?: string;
}

/**
 * User context for audit logging
 */
export interface UserContext {
  userId: string;
  userName?: string;
  userRole?: string;
  organizationId?: string;
  vesselId?: string;
  ipAddress?: string;
  userAgent?: string;
}

export abstract class BaseRepository<T extends BaseEntity> {
  protected table: Table<T, string>;
  protected tableName: string;
  protected userContext?: UserContext;

  constructor(table: Table<T, string>, tableName: string) {
    this.table = table;
    this.tableName = tableName;
  }

  /**
   * Set user context for audit logging
   */
  setUserContext(context: UserContext): void {
    this.userContext = context;
  }

  /**
   * Get current user context (for audit logging)
   */
  protected getUserContext(): UserContext | null {
    return this.userContext || null;
  }

  /**
   * Find record by ID
   * Returns null if not found or soft-deleted (instead of undefined) for consistency
   */
  async findById(id: string, includeDeleted: boolean = false): Promise<T | null> {
    // Handle null/undefined IDs gracefully
    if (id == null || id === '') {
      return null;
    }
    const result = await this.table.get(id);
    if (!result) return null;
    
    // Check if soft-deleted
    if (!includeDeleted && (result as any).deletedAt) {
      return null;
    }
    
    return result;
  }

  /**
   * Alias for findById (backward compatibility)
   */
  async getById(id: string): Promise<T | null> {
    return await this.findById(id);
  }

  /**
   * Alias for findAll (backward compatibility)
   */
  async getAll(): Promise<T[]> {
    const result = await this.findAll();
    return result.data;
  }

  /**
   * Find all records with optional filtering
   * By default, excludes soft-deleted records (deletedAt is null/undefined)
   */
  async findAll(options?: QueryOptions & { includeDeleted?: boolean }): Promise<QueryResult<T>> {
    let collection = this.table.toCollection();

    // By default, exclude soft-deleted records
    if (!options?.includeDeleted) {
      collection = collection.filter(item => {
        const deletedAt = (item as any).deletedAt;
        return deletedAt === null || deletedAt === undefined;
      });
    }

    // Merge convenience properties into filters
    const effectiveFilters = { ...options?.filters };
    if (options?.logbookType) {
      effectiveFilters.logbookType = options.logbookType;
    }

    // Apply filters
    if (Object.keys(effectiveFilters).length > 0) {
      Object.entries(effectiveFilters).forEach(([key, value]) => {
        collection = collection.filter(item => {
          const itemValue = (item as any)[key];
          if (Array.isArray(value)) {
            return value.includes(itemValue);
          }
          return itemValue === value;
        });
      });
    }

    // Get total count before pagination
    const total = await collection.count();

    // Apply sorting
    let data: T[];
    if (options?.sortBy) {
      const sortBy = options.sortBy;
      const sortOrder = options.sortOrder || 'asc';
      data = await collection.sortBy(sortBy);
      
      if (sortOrder === 'desc') {
        data = data.reverse();
      }
      
      return this.applyPagination(data, total, options);
    }

    // Apply pagination
    if (options?.offset !== undefined) {
      collection = collection.offset(options.offset);
    }
    if (options?.limit !== undefined) {
      collection = collection.limit(options.limit);
    }

    data = await collection.toArray();

    return {
      data,
      total,
      hasMore: (options?.offset || 0) + data.length < total,
    };
  }

  /**
   * Apply pagination to array result
   */
  private applyPagination(data: T[], total: number, options?: QueryOptions): QueryResult<T> {
    let result = data;

    if (options?.offset !== undefined) {
      result = result.slice(options.offset);
    }
    if (options?.limit !== undefined) {
      result = result.slice(0, options.limit);
    }

    return {
      data: result,
      total,
      hasMore: (options?.offset || 0) + result.length < total,
    };
  }

  /**
   * Create new record
   * Automatically logs to audit trail
   */
  async create(data: T, reason?: string): Promise<T> {
    const id = (data as any).id || this.generateId();
    const now = new Date();
    const record = { 
      ...data, 
      id,
      deletedAt: null, // Explicitly set to null (not deleted)
      createdAt: (data as any).createdAt || now,
      updatedAt: (data as any).updatedAt || now,
    } as T;
    
    await this.table.add(record);
    
    // Log to audit trail
    const userContext = this.getUserContext();
    if (userContext) {
      await AuditTrailService.logChange({
        entityType: this.tableName,
        entityId: id,
        operation: 'create',
        userId: userContext.userId,
        userName: userContext.userName,
        userRole: userContext.userRole,
        afterState: record,
        reason,
        ipAddress: userContext.ipAddress,
        userAgent: userContext.userAgent,
        organizationId: userContext.organizationId || (record as any).organizationId,
        vesselId: userContext.vesselId || (record as any).vesselId,
      });
    }
    
    return record;
  }

  /**
   * Update existing record
   * Automatically logs to audit trail with before/after state
   */
  async update(id: string, data: Partial<T>, reason?: string): Promise<T> {
    const existing = await this.findById(id, true); // Include deleted for update
    if (!existing) {
      throw new Error(`Record not found: ${id}`);
    }

    // Detect changed fields
    const changedFields = AuditTrailService.detectChangedFields(existing, { ...existing, ...data });
    
    const updated = { 
      ...existing, 
      ...data, 
      updatedAt: new Date(),
      deletedAt: (data as any).deletedAt !== undefined ? (data as any).deletedAt : existing.deletedAt,
    } as T;
    
    await this.table.put(updated);
    
    // Log to audit trail
    const userContext = this.getUserContext();
    if (userContext) {
      await AuditTrailService.logChange({
        entityType: this.tableName,
        entityId: id,
        operation: 'update',
        userId: userContext.userId,
        userName: userContext.userName,
        userRole: userContext.userRole,
        beforeState: existing,
        afterState: updated,
        changedFields,
        reason,
        ipAddress: userContext.ipAddress,
        userAgent: userContext.userAgent,
        organizationId: userContext.organizationId || (updated as any).organizationId,
        vesselId: userContext.vesselId || (updated as any).vesselId,
      });
    }
    
    return updated;
  }

  /**
   * Soft delete record (sets deletedAt timestamp)
   * NO HARD DELETES - Records are never permanently removed
   * Automatically logs to audit trail
   */
  async delete(id: string, reason?: string): Promise<boolean> {
    const existing = await this.findById(id, true); // Include deleted to check
    if (!existing) {
      throw new Error(`Record not found: ${id}`);
    }

    // Soft delete - set deletedAt timestamp
    const deleted = {
      ...existing,
      deletedAt: new Date(),
      updatedAt: new Date(),
    } as T;

    await this.table.put(deleted);
    
    // Log to audit trail
    const userContext = this.getUserContext();
    if (userContext) {
      await AuditTrailService.logChange({
        entityType: this.tableName,
        entityId: id,
        operation: 'delete',
        userId: userContext.userId,
        userName: userContext.userName,
        userRole: userContext.userRole,
        beforeState: existing,
        afterState: deleted,
        reason: reason || 'Record deleted',
        ipAddress: userContext.ipAddress,
        userAgent: userContext.userAgent,
        organizationId: userContext.organizationId || (existing as any).organizationId,
        vesselId: userContext.vesselId || (existing as any).vesselId,
      });
    }
    
    return true;
  }

  /**
   * Restore soft-deleted record (removes deletedAt timestamp)
   * Automatically logs to audit trail
   */
  async restore(id: string, reason?: string): Promise<T> {
    const existing = await this.findById(id, true); // Must include deleted
    if (!existing) {
      throw new Error(`Record not found: ${id}`);
    }

    if (!(existing as any).deletedAt) {
      throw new Error(`Record is not deleted: ${id}`);
    }

    const restored = {
      ...existing,
      deletedAt: null,
      updatedAt: new Date(),
    } as T;

    await this.table.put(restored);
    
    // Log to audit trail
    const userContext = this.getUserContext();
    if (userContext) {
      await AuditTrailService.logChange({
        entityType: this.tableName,
        entityId: id,
        operation: 'restore',
        userId: userContext.userId,
        userName: userContext.userName,
        userRole: userContext.userRole,
        beforeState: existing,
        afterState: restored,
        reason: reason || 'Record restored',
        ipAddress: userContext.ipAddress,
        userAgent: userContext.userAgent,
        organizationId: userContext.organizationId || (existing as any).organizationId,
        vesselId: userContext.vesselId || (existing as any).vesselId,
      });
    }
    
    return restored;
  }

  /**
   * Count records (excludes soft-deleted by default)
   */
  async count(filters?: Record<string, any>, includeDeleted: boolean = false): Promise<number> {
    let collection = this.table.toCollection();
    
    // Exclude soft-deleted by default
    if (!includeDeleted) {
      collection = collection.filter(item => {
        const deletedAt = (item as any).deletedAt;
        return deletedAt === null || deletedAt === undefined;
      });
    }
    
    if (filters && Object.keys(filters).length > 0) {
      Object.entries(filters).forEach(([key, value]) => {
        collection = collection.filter(item => {
          const itemValue = (item as any)[key];
          if (Array.isArray(value)) {
            return value.includes(itemValue);
          }
          return itemValue === value;
        });
      });
    }

    return await collection.count();
  }

  /**
   * Check if record exists
   */
  async exists(id: string): Promise<boolean> {
    const record = await this.findById(id);
    return record !== null;
  }

  /**
   * Find one record matching criteria
   */
  async findOne(filters: Record<string, any>): Promise<T | undefined> {
    let collection = this.table.toCollection();

    Object.entries(filters).forEach(([key, value]) => {
      collection = collection.filter(item => (item as any)[key] === value);
    });

    return await collection.first();
  }

  /**
   * Find many records matching criteria (excludes soft-deleted by default)
   */
  async findMany(filters: Record<string, any>, options?: QueryOptions & { includeDeleted?: boolean }): Promise<T[]> {
    let collection = this.table.toCollection();

    // Exclude soft-deleted by default
    if (!options?.includeDeleted) {
      collection = collection.filter(item => {
        const deletedAt = (item as any).deletedAt;
        return deletedAt === null || deletedAt === undefined;
      });
    }

    Object.entries(filters).forEach(([key, value]) => {
      collection = collection.filter(item => {
        const itemValue = (item as any)[key];
        if (Array.isArray(value)) {
          return value.includes(itemValue);
        }
        return itemValue === value;
      });
    });

    // Apply pagination
    if (options?.offset !== undefined) {
      collection = collection.offset(options.offset);
    }
    if (options?.limit !== undefined) {
      collection = collection.limit(options.limit);
    }

    return await collection.toArray();
  }

  /**
   * Batch create
   */
  async createMany(data: T[]): Promise<T[]> {
    const records = data.map(item => {
      const id = (item as any).id || this.generateId();
      return { ...item, id } as T;
    });

    await this.table.bulkAdd(records);
    return records;
  }

  /**
   * Batch update
   */
  async updateMany(filters: Record<string, any>, data: Partial<T>): Promise<number> {
    const records = await this.findMany(filters);
    
    const updates = records.map(record => ({
      ...record,
      ...data,
      updatedAt: new Date(),
    }));

    await this.table.bulkPut(updates);
    return updates.length;
  }

  /**
   * Batch soft delete (sets deletedAt timestamp for multiple records)
   * NO HARD DELETES - Records are never permanently removed
   */
  async deleteMany(filters: Record<string, any>, reason?: string): Promise<number> {
    const records = await this.findMany(filters, { includeDeleted: true });
    const now = new Date();
    
    const deletedRecords = records.map(record => ({
      ...record,
      deletedAt: now,
      updatedAt: now,
    })) as T[];

    await this.table.bulkPut(deletedRecords);
    
    // Log each deletion to audit trail
    const userContext = this.getUserContext();
    if (userContext) {
      for (const record of records) {
        await AuditTrailService.logChange({
          entityType: this.tableName,
          entityId: (record as any).id,
          operation: 'delete',
          userId: userContext.userId,
          userName: userContext.userName,
          userRole: userContext.userRole,
          beforeState: record,
          afterState: deletedRecords.find(r => (r as any).id === (record as any).id),
          reason: reason || 'Batch delete',
          ipAddress: userContext.ipAddress,
          userAgent: userContext.userAgent,
          organizationId: userContext.organizationId || (record as any).organizationId,
          vesselId: userContext.vesselId || (record as any).vesselId,
        });
      }
    }
    
    return deletedRecords.length;
  }

  /**
   * Clear all records
   */
  async clear(): Promise<void> {
    await this.table.clear();
  }

  /**
   * Get all records as array
   */
  async toArray(): Promise<T[]> {
    return await this.table.toArray();
  }

  /**
   * Generate unique ID
   */
  protected generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}


